/**
 * Exception Type Toggle Chips Component
 * Row of clickable chips for selecting exception types within umbrella reports.
 * All options visible at a glance — click to toggle on/off.
 */

import React from 'react'
import { Stack, Chip, Typography, Box } from '@mui/material'

interface ExceptionTypeOption {
  key: string
  label: string
}

interface ExceptionTypeMultiSelectProps {
  value: string
  onChange: (value: string) => void
  options: ExceptionTypeOption[]
  disabled?: boolean
  label?: string
}

const TEAL = '#007f98'

const labelStyle = {
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '0.75rem',
  mb: 0.5,
  display: 'block',
}

const ExceptionTypeMultiSelect: React.FC<ExceptionTypeMultiSelectProps> = ({
  value,
  onChange,
  options,
  disabled = false,
  label = 'Exception Type',
}) => {
  const selectedValues = value ? value.split(',') : []

  const handleChipClick = (key: string) => {
    const isSelected = selectedValues.includes(key)
    if (isSelected) {
      onChange(selectedValues.filter(v => v !== key).join(','))
    } else {
      onChange([...selectedValues, key].join(','))
    }
  }

  const chipSx = (selected: boolean) => ({
    fontWeight: 500,
    fontSize: '0.8125rem',
    cursor: disabled ? 'default' : 'pointer',
    ...(selected
      ? {
          backgroundColor: TEAL,
          color: '#FFFFFF',
          '&:hover': { backgroundColor: '#006680' },
        }
      : {
          backgroundColor: 'transparent',
          color: 'text.primary',
          border: '1px solid #C0C0C0',
          '&:hover': { backgroundColor: '#F5F5F5' },
        }),
  })

  return (
    <Box>
      {label && (
        <Typography component="label" sx={labelStyle}>
          {label}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {options.map((option) => {
          const selected = selectedValues.includes(option.key)
          return (
            <Chip
              key={option.key}
              label={option.label}
              variant={selected ? 'filled' : 'outlined'}
              onClick={disabled ? undefined : () => handleChipClick(option.key)}
              sx={chipSx(selected)}
            />
          )
        })}
      </Stack>
    </Box>
  )
}

export default ExceptionTypeMultiSelect
